import { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import {
  mdiPlus,
  mdiMapMarkerOutline,
  mdiAccountOutline,
  mdiDeleteOutline,
  mdiClose,
  mdiContentCopy,
  mdiAlertCircleOutline,
} from "@mdi/js";
import {
  Button,
  Drawer,
  Input,
  InputGroup,
  Spinner,
  Switch,
  TextField,
} from "@heroui/react";
import { serviceCenterFormStore } from "@/stores/ServiceCenterFormStore";
import LeafletMap from "./LeafletMap";
import "./ServiceCenterDrawer.css";

interface ServiceCenterDrawerProps {
  onSuccess?: () => void;
}

const ServiceCenterDrawer: React.FC<ServiceCenterDrawerProps> = observer(
  ({ onSuccess }) => {
    const store = serviceCenterFormStore;
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Close suggestions on outside click
    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (
          suggestionsRef.current &&
          !suggestionsRef.current.contains(e.target as Node)
        ) {
          store.hideSuggestions();
        }
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleMapPositionChange = (lat: number, lon: number) => {
      store.setMapPosition(lat, lon);
    };

    const handleClose = () => store.close();

    const handleSave = () => {
      store.submit(onSuccess);
    };

    const getFieldError = (field: string): string | undefined => {
      return store.submitAttempted ? store.validationErrors[field] : undefined;
    };

    const getAdminError = (
      adminId: string,
      field: string,
    ): string | undefined => {
      return store.submitAttempted
        ? store.adminErrors[adminId]?.[field]
        : undefined;
    };

    return (
      <Drawer.Backdrop
        isOpen={store.isOpen}
        onOpenChange={(v) => {
          if (!v) handleClose();
        }}
        className="sc-drawer-backdrop"
      >
        <Drawer.Content placement="right" className="sc-drawer-content">
          <Drawer.Dialog className="sc-drawer-dialog">
            <Drawer.CloseTrigger />
            <Drawer.Header className="sc-drawer-header">
              <Drawer.Heading>
                {store.isEditing
                  ? "Редактирование контрагента"
                  : "Новый контрагент"}
              </Drawer.Heading>
              <p className="sc-drawer-subtitle">
                {store.isEditing
                  ? "Измените данные сервисного центра"
                  : "Заполните данные для добавления нового сервисного центра"}
              </p>
            </Drawer.Header>

            <Drawer.Body className="sc-drawer-body">
              {/* ── Основная информация ── */}
              <div className="sc-drawer-section">
                <span className="sc-drawer-section__title">
                  Основная информация
                </span>
                <Switch
                  key={store.editUuid ?? "new"}
                  isSelected={Boolean(store.isPublished)}
                  onChange={(v) => store.setIsPublished(v)}
                  className="sc-drawer-published-switch"
                >
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                  <Switch.Content className="sc-drawer-published-label">
                    {store.isPublished ? "Опубликован" : "Не опубликован"}
                  </Switch.Content>
                </Switch>
              </div>

              <div className="sc-drawer-field">
                <label className="sc-drawer-field__label">
                  Название <span className="sc-drawer-field__req">*</span>
                </label>
                <TextField fullWidth isInvalid={!!getFieldError("name")}>
                  <Input
                    placeholder="Название сервисного центра"
                    value={store.name}
                    maxLength={255}
                    onChange={(e) => store.setName(e.target.value)}
                  />
                </TextField>
                {getFieldError("name") && (
                  <span className="sc-drawer-field__error">
                    {getFieldError("name")}
                  </span>
                )}
              </div>

              {/* ── Адрес с автокомплитом ── */}
              <div className="sc-drawer-field">
                <label className="sc-drawer-field__label">
                  Адрес <span className="sc-drawer-field__req">*</span>
                </label>
                <div className="sc-drawer-address-wrap" ref={suggestionsRef}>
                  <TextField fullWidth isInvalid={!!getFieldError("address")}>
                    <Input
                      placeholder="Начните вводить адрес..."
                      value={store.addressQuery}
                      onChange={(e) => store.setAddressQuery(e.target.value)}
                      onFocus={() => {
                        if (store.suggestions.length > 0) {
                          store.setShowSuggestions(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on suggestion to fire first
                        setTimeout(() => store.prepareSuggestionBanner(), 200);
                      }}
                    />
                  </TextField>
                  {store.showSuggestions && store.suggestions.length > 0 && (
                    <div className="sc-drawer-suggestions">
                      {store.suggestions.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          className="sc-drawer-suggestion"
                          onClick={() => store.selectSuggestion(s)}
                        >
                          <Icon
                            path={mdiMapMarkerOutline}
                            size={0.65}
                            className="sc-drawer-suggestion__icon"
                          />
                          <span className="sc-drawer-suggestion__text">
                            {s.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {getFieldError("address") && (
                  <span className="sc-drawer-field__error">
                    {getFieldError("address")}
                  </span>
                )}

                {/* Banner: marker dragged — offer to update address */}
                {store.pendingReverseAddress && (
                  <div className="sc-drawer-geo-hint">
                    <Icon
                      path={mdiMapMarkerOutline}
                      size={0.65}
                      className="sc-drawer-geo-hint__icon"
                    />
                    <span className="sc-drawer-geo-hint__text">
                      Точка перемещена:{" "}
                      <strong>{store.pendingReverseAddress}</strong>
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onPress={() => store.applyReverseAddress()}
                    >
                      Обновить адрес
                    </Button>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label="Отклонить"
                      onPress={() => store.dismissReverseAddress()}
                      className="sc-drawer-geo-hint__dismiss"
                    >
                      <Icon path={mdiClose} size={0.65} />
                    </Button>
                  </div>
                )}

                {/* Banner: address typed — offer to move pin */}
                {store.pendingSuggestedCoords && (
                  <div className="sc-drawer-geo-hint">
                    <Icon
                      path={mdiMapMarkerOutline}
                      size={0.65}
                      className="sc-drawer-geo-hint__icon"
                    />
                    <span className="sc-drawer-geo-hint__text">
                      Найден адрес:{" "}
                      <strong>{store.pendingSuggestedCoords.label}</strong>
                    </span>
                    <Button
                      variant="primary"
                      size="sm"
                      onPress={() => store.applyAddressToPoint()}
                    >
                      Переставить точку
                    </Button>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label="Отклонить"
                      onPress={() => store.dismissAddressToPoint()}
                      className="sc-drawer-geo-hint__dismiss"
                    >
                      <Icon path={mdiClose} size={0.65} />
                    </Button>
                  </div>
                )}
              </div>

              {/* ── Координаты ── */}
              <div className="sc-drawer-field">
                <label className="sc-drawer-field__label">Координаты</label>
                <p className="sc-drawer-field__hint">
                  Координаты устанавливаются с карты. Скопировать:
                </p>
                <div className="sc-drawer-coords">
                  <TextField fullWidth>
                    <InputGroup fullWidth>
                      <InputGroup.Input
                        readOnly
                        value={store.lat.toFixed(6)}
                        placeholder="Широта"
                        aria-label="Широта"
                      />
                      <InputGroup.Suffix className="pr-0">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="ghost"
                          aria-label="Скопировать широту"
                          onPress={() =>
                            navigator.clipboard.writeText(store.lat.toFixed(6))
                          }
                        >
                          <Icon path={mdiContentCopy} size={0.65} />
                        </Button>
                      </InputGroup.Suffix>
                    </InputGroup>
                  </TextField>
                  <TextField fullWidth>
                    <InputGroup fullWidth>
                      <InputGroup.Input
                        readOnly
                        value={store.lon.toFixed(6)}
                        placeholder="Долгота"
                        aria-label="Долгота"
                      />
                      <InputGroup.Suffix className="pr-0">
                        <Button
                          isIconOnly
                          size="sm"
                          variant="ghost"
                          aria-label="Скопировать долготу"
                          onPress={() =>
                            navigator.clipboard.writeText(store.lon.toFixed(6))
                          }
                        >
                          <Icon path={mdiContentCopy} size={0.65} />
                        </Button>
                      </InputGroup.Suffix>
                    </InputGroup>
                  </TextField>
                </div>
              </div>

              {/* ── Карта ── */}
              <div className="sc-drawer-field">
                <label className="sc-drawer-field__label">
                  Расположение на карте
                </label>
                <p className="sc-drawer-field__hint">
                  Перетащите маркер для уточнения местоположения
                </p>
                {store.isOpen && (
                  <LeafletMap
                    lat={store.lat}
                    lon={store.lon}
                    onPositionChange={handleMapPositionChange}
                  />
                )}
              </div>

              {/* ── Администраторы ── */}
              <div className="sc-drawer-section">
                <span className="sc-drawer-section__title">Администраторы</span>
                <Button variant="tertiary" size="sm" onPress={store.addAdmin}>
                  <Icon path={mdiPlus} size={0.65} />
                  Добавить
                </Button>
              </div>

              {store.admins.length === 0 && (
                <div className="sc-drawer-empty-admins">
                  <Icon path={mdiAccountOutline} size={1.2} />
                  <span>Администраторы не добавлены</span>
                </div>
              )}

              {store.admins.map((admin, idx) => (
                <div key={admin.id} className="sc-drawer-admin-row">
                  <div className="sc-drawer-admin-row__header">
                    <span className="sc-drawer-admin-row__label">
                      <Icon path={mdiAccountOutline} size={0.6} />
                      Администратор #{idx + 1}
                      {admin.isExisting && (
                        <span className="sc-drawer-admin-existing">
                          (существующий)
                        </span>
                      )}
                    </span>
                    <Button
                      isIconOnly
                      variant="ghost"
                      size="sm"
                      aria-label="Удалить администратора"
                      onPress={() => store.removeAdmin(admin.id)}
                      className="sc-drawer-admin-row__delete"
                    >
                      <Icon path={mdiDeleteOutline} size={0.7} />
                    </Button>
                  </div>
                  {admin.isExisting ? (
                    <div className="sc-drawer-admin-existing-info">
                      <span>{admin.phone}</span>
                      {store.adminSubmitErrors[admin.id] && (
                        <span className="sc-drawer-field__error">
                          {store.adminSubmitErrors[admin.id]}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="sc-drawer-admin-fields">
                      <div
                        className={`sc-drawer-phone-group${
                          getAdminError(admin.id, "phone") ||
                          store.adminSubmitErrors[admin.id]
                            ? " sc-drawer-phone-group--invalid"
                            : ""
                        }`}
                      >
                        <div className="sc-drawer-phone-cc">
                          <span className="sc-drawer-phone-cc__plus">+</span>
                          <input
                            className="sc-drawer-phone-cc__input"
                            value={admin.phoneRegion ?? "7"}
                            maxLength={3}
                            inputMode="numeric"
                            aria-label="Код страны"
                            onChange={(e) => {
                              const digits = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 3);
                              store.updateAdminRegion(admin.id, digits);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === " ") e.preventDefault();
                            }}
                          />
                        </div>
                        <span
                          className="sc-drawer-phone-sep"
                          aria-hidden="true"
                        />
                        <input
                          className="sc-drawer-phone-local"
                          placeholder="XXXXXXXXXX"
                          value={admin.phone}
                          inputMode="numeric"
                          maxLength={Math.max(
                            7,
                            11 - (admin.phoneRegion ?? "7").length,
                          )}
                          onChange={(e) => {
                            const max = Math.max(
                              7,
                              11 - (admin.phoneRegion ?? "7").length,
                            );
                            const digits = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, max);
                            store.updateAdminPhone(admin.id, digits);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === " ") e.preventDefault();
                          }}
                        />
                      </div>
                      {(getAdminError(admin.id, "phone") ||
                        store.adminSubmitErrors[admin.id]) && (
                        <span className="sc-drawer-field__error">
                          {getAdminError(admin.id, "phone") ??
                            store.adminSubmitErrors[admin.id]}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </Drawer.Body>

            <Drawer.Footer className="sc-drawer-footer">
              {store.submitError && (
                <div className="sc-drawer-submit-error">
                  <Icon
                    path={mdiAlertCircleOutline}
                    size={0.75}
                    className="sc-drawer-submit-error__icon"
                  />
                  <span>{store.submitError}</span>
                </div>
              )}
              <div className="sc-drawer-footer__actions">
                <Button variant="tertiary" onPress={handleClose}>
                  Отменить
                </Button>
                <div className="sc-drawer-footer__spacer" />
                <Button
                  variant="primary"
                  onPress={handleSave}
                  isDisabled={!store.isValid || store.isSaving}
                  isPending={store.isSaving}
                >
                  {({ isPending }) => (
                    <>
                      {isPending && <Spinner color="current" size="sm" />}
                      {store.isEditing ? "Сохранить" : "Создать"}
                    </>
                  )}
                </Button>
              </div>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    );
  },
);

ServiceCenterDrawer.displayName = "ServiceCenterDrawer";
export default ServiceCenterDrawer;
